import {
    BetSlipError,
    BetSlipErrorType,
    BonusBetSlipError,
    BonusErrorType,
    SelectionError,
    SelectionErrorType,
    SelectionMaxSelectionForMarketError,
} from '@superbet-group/betting.lib.validation';
import {
    SelectionSourceScreen,
    SelectionSourceType,
    SelectionType,
} from '@superbet-group/web.types';

import Odd from '@core/models/offer/Odd';
import OfferEvent from '@core/models/offer/OfferEvent';
import ISelection from '@models/betSlip/ISelection';
import SportSelection from '@models/betSlip/SportSelection';
import IBetSlipError from '@models/betSlip/validation/IBetSlipError';
import { BetSlipErrorType as ErrorType } from '@models/shared/betSlip/betSlipEnums';
import BetSlipOdd from '@core/models/base/BetSlipOdd';

function findSelectionsByEventId(selections: ISelection[], eventId: number, type: SelectionType): ISelection[] {
    return selections.filter((s) => s.getEventId() === eventId && s.getItemType() === type);
}

function findSelectionIndexByEventId(selections: ISelection[], eventId: number, type: SelectionType) {
    return selections.findIndex((s) => (s.getEventId() === eventId && s.getItemType() === type));
}

export function findSelectionIndex(selections: ISelection[], selection: ISelection) {
    return selections.findIndex((s) => s.getOddUuid() === selection.getOddUuid());
}

function recreateSelection({
    event,
    oddId,
    stashedOdd,
    specialBetValue,
    sourceType,
    sourceScreen,
}: {
    event: OfferEvent;
    oddId?: number | null;
    stashedOdd?: BetSlipOdd | null;
    specialBetValue?: string;
    sourceType?: SelectionSourceType,
    sourceScreen?: SelectionSourceScreen,
}) {
    const odd = oddId ? event.getOddById(oddId, specialBetValue) : undefined;

    const sportSelection = SportSelection.createItem(event, odd as Odd, sourceType, sourceScreen);

    if (!sportSelection.odd && stashedOdd) {
        sportSelection.odd = stashedOdd;
    }

    return sportSelection;
}

function getBetSlipErrorMessageTemplate(betSlipError: ValidationError): string {
    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (betSlipError.type) {
        case ErrorType.errorsInSelection:
            return 'check your events, you have errors';
        case ErrorType.chooseTicketNegotiation:
            return 'you must accept/decline ticket negotiation';
        case BetSlipErrorType.UserBalanceInsufficient:
            return 'pbp.betslip.error_insufficient_funds';
        case BetSlipErrorType.EmptyBetSlip:
            return 'no events on ticket';
        case BetSlipErrorType.MinimumBetSlipStakeError:
            return 'Minimum stake is {minimumStake}';
        case BetSlipErrorType.MaximumBetSlipStakeError:
            return 'Maximum stake is {maximumStake}';
        case BetSlipErrorType.MinimumBetSlipStakePerCombinationError:
            return 'The minimum stake per combination is {minimumStakePerCombination}';
        case BetSlipErrorType.LiveEventOnOfflineType:
            return 'live events are not allowed on offline betSlip';
        case ErrorType.noSelectedSystemCombination:
        case BetSlipErrorType.NoSelectedSystemCombination:
            return 'please select at least one combination';
        case BetSlipErrorType.MaxNumberOfSelections:
            return 'maximum number of selections reached';
        case SelectionErrorType.SelectionIsLocked:
            return 'event/odd is blocked';
        case SelectionErrorType.OddIsFinished:
            return 'selected odd doesn\'t exist anymore';
        case SelectionErrorType.OddIsUnavailable:
            return 'selected odd is unavailable';
        case SelectionErrorType.EventIsFinished:
            return 'event has finished';
        case SelectionErrorType.SelectionIsUnavailable:
            return 'selection is unavailable';
        case ErrorType.eventCantPlaceBets:
            return 'you can not place bets for this event anymore';
        case SelectionErrorType.MinimumStake:
            return 'your stake is too small';
        case SelectionErrorType.MaximalStake:
            return 'your stake is too big, maximal is {maximum}';
        case SelectionErrorType.MinimumStakeRaised:
            return 'minimum stake on ticket raised';
        case SelectionErrorType.SameMarket:
            return 'it is not possible to combine same market';
        case SelectionErrorType.CantCombineEvents:
            return 'It is not possible to combine this event with events you already have on your betslip';
        case SelectionErrorType.CantCombineMarkets:
            return 'It is not possible to combine this market with markets you already have on your betslip';
        case ErrorType.minSystemToPlay:
            return 'minimum system to select is {minNumber}/{fromNumber}';
        case SelectionErrorType.CantCombine:
            return 'It is not possible to combine this with events you already have on your betslip';
        case SelectionErrorType.AddMoreFromSameMarket:
            return 'put more events of same market';
        case SelectionErrorType.CantAddMoreFromThisMarket:
            if ((betSlipError as SelectionMaxSelectionForMarketError).params.maxSelectionForMarket > 1) {
                return 'You are not allowed to add more'
                    + ' than {maxSelectionForMarket} events from this type of market';
            }
            return 'You are not allowed to add more than one event from this type of market';
        case SelectionErrorType.PutMoreOdds:
            return 'This event requires that you add'
                + ' {minNonPromotionalSelection} more events with odd even or greater than {minNonPromotionalOdd}';
        case SelectionErrorType.SuperExtraBelowMinimum:
            return 'Super Extra must be combined with'
                + ' at least {minNonPromotionalSelection} other events with a minimum odd {minNonPromotionalOdd}';
        case SelectionErrorType.AddMoreFromOtherMarket:
            return 'put more events of other markets';
        case SelectionErrorType.CantAddMoreFromSameEvent:
            return 'pbp.betslip.error_max_selections_of_type';
        case BetSlipErrorType.MaxWinAmount:
            return 'maximal win amount of {maxWinAmount} reached';
        case ErrorType.maxBetSlips:
            return 'maxBetSlips';
        case BonusErrorType.UserBonusBalance:
            return 'pam.withdrawal.errors.insufficient_funds';
        case BonusErrorType.FreeBetSystemBet:
            return 'bonus.betslip.error_free_bet_simple';
        case BonusErrorType.MinMultipleTicketEvents:
            return 'bonus.betslip.error_min_events_multiple';
        case BonusErrorType.MinMultipleTicketOdd:
            return 'bonus.betslip.error_min_ticket_odd_multiple';
        case BonusErrorType.MinEventOddInMultipleTicket:
            return 'bonus.betslip.error_min_event_odd_multiple';
        case BonusErrorType.EligibleEvents:
            return 'bonus.betslip.error_event_eligible';
        case BonusErrorType.EligibleMarket:
            return 'bonus.betslip.error_market_eligible';
        case ErrorType.winLowerThanStake:
        case BetSlipErrorType.WinLowerThanStake:
            return 'bonus.betslip.error_potential_win';
        case BonusErrorType.ExpirationDate:
            return 'bonus.betslip.error_bonus_expiration';
        case BonusErrorType.MarketOdd:
            return 'bonus.betslip.error_market_min_odd';
        case BonusErrorType.FreeBetRetail:
            return 'bonus.betslip.error_free_bet_retail';
        case BonusErrorType.SportRetail:
            return 'bonus.betslip.error_sport_retail';
        case BonusErrorType.MinimumTicketOdd:
            return 'bonus.betslip.error_min_ticket_odd';
        case BonusErrorType.MinimumEventOdd:
            return 'bonus.betslip.error_min_event_odd';
        default:
            return '';
    }
}

export function isValidationErrorWithParams(error: ValidationError): error is IBetSlipError {
    return (error as any).params !== undefined;
}

export type ValidationError =
    | IBetSlipError
    | BetSlipError
    | SelectionError
    | BonusBetSlipError;

export {
    findSelectionsByEventId,
    findSelectionIndexByEventId,
    recreateSelection,
    getBetSlipErrorMessageTemplate,
};
